import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  FileText, Upload, Trash2, Download, AlertTriangle, CheckCircle,
  Calendar, Loader2, Plus, Eye, Shield, Pencil,
} from 'lucide-react';
import { authedUrl } from '../lib/authSession';

// Iter 42 — accepted image MIME types that we auto-wrap into a single-page PDF
// client-side before sending to the backend analyze/upload endpoints. Keeping
// the conversion on the frontend means the backend keeps its simple "PDF only"
// contract.
const IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

/**
 * Load an image File into an HTMLImageElement so we can size the PDF page.
 */
const loadImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => resolve({ img, dataUrl: reader.result });
    img.onerror = () => reject(new Error('Could not decode image'));
    img.src = reader.result;
  };
  reader.onerror = () => reject(new Error('Could not read image file'));
  reader.readAsDataURL(file);
});

/**
 * Auto-convert a JPG/PNG File into a single-page PDF File of the same
 * basename. The page is letter-portrait and the image is fit-contained
 * with a small margin so the AI OCR has clean whitespace around it.
 */
const convertImageToPdfFile = async (file) => {
  const { img, dataUrl } = await loadImage(file);
  // Build a letter-size PDF in pt (612 x 792)
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 36;
  const maxW = pageW - margin * 2;
  const maxH = pageH - margin * 2;
  const ratio = Math.min(maxW / img.width, maxH / img.height);
  const drawW = img.width * ratio;
  const drawH = img.height * ratio;
  const x = (pageW - drawW) / 2;
  const y = (pageH - drawH) / 2;
  const imgFmt = file.type === 'image/png' ? 'PNG' : 'JPEG';
  pdf.addImage(dataUrl, imgFmt, x, y, drawW, drawH, undefined, 'FAST');
  const blob = pdf.output('blob');
  const newName = file.name.replace(/\.(jpe?g|png)$/i, '') + '.pdf';
  return new File([blob], newName, { type: 'application/pdf' });
};

/**
 * AgentDocumentsTab
 * --------------------------------------------------------------
 * A reusable "My Documents" panel for the Agent Portal and also
 * embeddable in the Admin → Agent detail view.
 *
 * Three modes:
 *   - default (agent self-service):  upload / edit / delete own docs
 *   - readOnly:                       view only, no buttons
 *   - adminMode (with adminPassword): admin gets full CRUD on any agent's docs
 *
 * Documents are PDFs (≤ 15 MB) stored in Emergent Object Storage —
 * they survive container redeploys. Soft-delete only.
 *
 * Props:
 *   agentId        — required
 *   apiUrl         — backend base (process.env.REACT_APP_BACKEND_URL)
 *   readOnly       — disables all mutations
 *   adminMode      — admin can upload/edit/delete on behalf of agent
 *   adminPassword  — required when adminMode=true; sent as X-Admin-Password
 */
export default function AgentDocumentsTab({
  agentId,
  apiUrl,
  readOnly = false,
  adminMode = false,
  adminPassword = null,
}) {
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [error, setError] = useState('');

  const isAdminListing = adminMode && adminPassword;
  const listUrl = isAdminListing
    ? `${apiUrl}/api/admin/agents/${agentId}/documents`
    : `${apiUrl}/api/agents/${agentId}/documents`;

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = isAdminListing ? { 'X-Admin-Password': adminPassword } : {};
      const [docsRes, typesRes] = await Promise.all([
        fetch(listUrl, { headers }),
        fetch(`${apiUrl}/api/agents/documents/types`),
      ]);
      if (!docsRes.ok) throw new Error(`Failed to load documents (${docsRes.status})`);
      setDocs(await docsRes.json());
      if (typesRes.ok) {
        const t = await typesRes.json();
        setTypes(t.types || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, listUrl, isAdminListing, adminPassword]);

  useEffect(() => { reload(); }, [reload]);

  const expiringSoon = useMemo(
    () => docs.filter(d => d.days_until_expiry != null && d.days_until_expiry <= 30),
    [docs]
  );

  const handleDelete = async (docId) => {
    if (!window.confirm('Permanently remove this document? This cannot be undone.')) return;
    try {
      const url = adminMode
        ? `${apiUrl}/api/admin/agents/documents/${docId}`
        : `${apiUrl}/api/agents/documents/${docId}`;
      const headers = adminMode && adminPassword ? { 'X-Admin-Password': adminPassword } : {};
      const res = await fetch(url, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Delete failed');
      setDocs(prev => prev.filter(d => d.id !== docId));
    } catch (err) {
      alert(err.message || 'Delete failed');
    }
  };

  const handleDownload = (doc) => {
    window.open(authedUrl(`${apiUrl}/api/agents/documents/${doc.id}/download`), '_blank');
  };

  const typeLabel = (val, customName) => {
    if (val === 'other' && customName) return customName;
    return types.find(t => t.value === val)?.label || val;
  };

  const canMutate = !readOnly;
  const showAdminBadge = adminMode && adminPassword;

  return (
    <div className="space-y-6" data-testid="agent-documents-tab">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-[#0077B6]" />
                {showAdminBadge ? 'Compliance Documents' : 'My Compliance Documents'}
                {showAdminBadge && (
                  <span className="bg-[#0077B6]/10 text-[#0077B6] text-xs font-semibold px-2 py-0.5 rounded-full ml-1">
                    Admin
                  </span>
                )}
              </CardTitle>
              <p className="text-sm text-gray-600 mt-2 max-w-2xl">
                {showAdminBadge
                  ? 'Manage this advisor’s LLQP license, E&O insurance, AML training certificates and any other compliance documents on their behalf.'
                  : 'Upload and manage your LLQP license, E&O insurance, AML training certificates and any other compliance documents. Files are stored securely on persistent cloud storage and remain available to you and the Utmost Financial admin team.'}
              </p>
            </div>
            {canMutate && (
              <Button
                onClick={() => setShowUpload(true)}
                className="bg-[#0077B6] hover:bg-[#005f8a]"
                data-testid="upload-document-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Upload Document
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-900 p-3 rounded mb-4 text-sm">
              {error}
            </div>
          )}

          {expiringSoon.length > 0 && (
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded p-3 mb-4 flex items-start gap-3" data-testid="expiring-banner">
              <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">
                  {expiringSoon.length} document{expiringSoon.length === 1 ? '' : 's'} expiring within 30 days
                </p>
                <p className="mt-0.5">Renew before the expiry date to keep the license in good standing.</p>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Loading documents…
            </div>
          ) : docs.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-1">No documents uploaded yet</p>
              {canMutate && (
                <p className="text-sm text-gray-400">
                  Click <b>Upload Document</b> above to add the first compliance file.
                </p>
              )}
            </div>
          ) : (
            <div className="grid gap-3" data-testid="documents-list">
              {docs.map(doc => <DocumentRow
                key={doc.id}
                doc={doc}
                typeLabel={typeLabel}
                onDownload={handleDownload}
                onEdit={canMutate ? setEditingDoc : null}
                onDelete={canMutate ? handleDelete : null}
              />)}
            </div>
          )}
        </CardContent>
      </Card>

      {showUpload && canMutate && (
        <UploadModal
          agentId={agentId}
          apiUrl={apiUrl}
          types={types}
          adminMode={adminMode}
          adminPassword={adminPassword}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); reload(); }}
        />
      )}

      {editingDoc && canMutate && (
        <EditModal
          doc={editingDoc}
          apiUrl={apiUrl}
          types={types}
          adminMode={adminMode}
          adminPassword={adminPassword}
          onClose={() => setEditingDoc(null)}
          onSaved={() => { setEditingDoc(null); reload(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// Subcomponents
// ============================================================
function DocumentRow({ doc, typeLabel, onDownload, onEdit, onDelete }) {
  const expSoon = doc.days_until_expiry != null && doc.days_until_expiry <= 30;
  const expired = doc.days_until_expiry != null && doc.days_until_expiry < 0;
  const fmtDate = (d) => {
    if (!d) return '—';
    // Parse YYYY-MM-DD as a *local* date to avoid UTC-midnight shifting the
    // displayed day by one in timezones west of UTC.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
    const dt = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(d);
    return dt.toLocaleDateString('en-CA');
  };
  const fmtSize = (b) => {
    if (!b) return '';
    const kb = b / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb.toFixed(0)} KB`;
  };

  return (
    <div
      className="border rounded-lg p-4 hover:shadow-md transition-shadow bg-white flex flex-col sm:flex-row sm:items-center gap-4"
      data-testid={`document-row-${doc.id}`}
    >
      <div className="flex-shrink-0">
        <div className="h-12 w-12 rounded-lg bg-red-50 flex items-center justify-center">
          <FileText className="h-6 w-6 text-red-500" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-gray-800 truncate">
            {typeLabel(doc.document_type, doc.custom_name)}
          </h3>
          {expired && (
            <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5 rounded-full">
              Expired
            </span>
          )}
          {!expired && expSoon && (
            <span className="bg-amber-100 text-amber-700 text-xs font-medium px-2 py-0.5 rounded-full">
              Expires in {doc.days_until_expiry}d
            </span>
          )}
          {!expSoon && !expired && doc.expiry_date && (
            <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              Valid
            </span>
          )}
          {doc.uploaded_by === 'admin' && (
            <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-0.5 rounded-full" title="Uploaded by admin">
              Admin upload
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 truncate mt-0.5" title={doc.original_filename}>
          {doc.original_filename} • {fmtSize(doc.size)}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
          {doc.issue_date && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Issued: {fmtDate(doc.issue_date)}
            </span>
          )}
          {doc.expiry_date && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Expires: {fmtDate(doc.expiry_date)}
            </span>
          )}
        </div>
        {doc.notes && (
          <p className="text-xs text-gray-600 mt-1.5 italic line-clamp-2">{doc.notes}</p>
        )}
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onDownload(doc)}
          data-testid={`preview-document-${doc.id}`}
          title="Preview / Download"
        >
          <Eye className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onDownload(doc)}
          data-testid={`download-document-${doc.id}`}
          title="Download"
        >
          <Download className="h-4 w-4" />
        </Button>
        {onEdit && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(doc)}
            data-testid={`edit-document-${doc.id}`}
            title="Edit metadata"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        {onDelete && (
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50"
            onClick={() => onDelete(doc.id)}
            data-testid={`delete-document-${doc.id}`}
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

function UploadModal({ agentId, apiUrl, types, adminMode, adminPassword, onClose, onUploaded }) {
  // Iter 41 — Multi-file upload with AI auto-detection.
  // Each entry: { id, file, status: 'queued'|'analyzing'|'ready'|'uploading'|'done'|'error',
  //               document_type, custom_name, issue_date, expiry_date, notes, confidence, error }
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);  // true while any AI analyze or upload is running
  const [topErr, setTopErr] = useState('');

  const addFiles = async (fileList) => {
    setTopErr('');
    const next = [];
    const skipped = [];
    for (let i = 0; i < fileList.length; i++) {
      const raw = fileList[i];
      let f = raw;
      // Iter 42 — auto-convert JPG / PNG to a single-page PDF on the fly
      if (raw.type !== 'application/pdf') {
        if (IMAGE_MIME_TYPES.includes(raw.type)) {
          try {
            f = await convertImageToPdfFile(raw);
          } catch (err) {
            skipped.push(`"${raw.name}" — image conversion failed (${err.message || 'unknown error'})`);
            continue;
          }
        } else {
          skipped.push(`"${raw.name}" — only PDF, JPG, or PNG files are accepted`);
          continue;
        }
      }
      if (f.size > 15 * 1024 * 1024) {
        skipped.push(`"${raw.name}" — exceeds 15 MB (post-conversion: ${(f.size/1024/1024).toFixed(1)} MB)`);
        continue;
      }
      next.push({
        id: `${Date.now()}-${i}-${f.name}`,
        file: f,
        originalName: raw.name !== f.name ? raw.name : null,
        status: 'queued',
        document_type: 'llqp_license',
        custom_name: '',
        issue_date: '',
        expiry_date: '',
        notes: '',
        confidence: '',
        error: '',
      });
    }
    if (skipped.length > 0) {
      setTopErr(`Skipped: ${skipped.join('; ')}`);
    }
    if (next.length === 0) return;
    setEntries(prev => [...prev, ...next]);
    runAnalyses(next);
  };

  const runAnalyses = async (newEntries) => {
    setBusy(true);
    for (const ent of newEntries) {
      setEntries(prev => prev.map(e => e.id === ent.id ? { ...e, status: 'analyzing' } : e));
      try {
        const fd = new FormData();
        fd.append('file', ent.file);
        const res = await fetch(`${apiUrl}/api/agents/${agentId}/documents/analyze`, {
          method: 'POST', body: fd,
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || `Analyze failed (${res.status})`);
        }
        const meta = await res.json();
        setEntries(prev => prev.map(e => e.id === ent.id ? {
          ...e,
          status: 'ready',
          document_type: meta.document_type || 'other',
          custom_name: meta.custom_name || '',
          issue_date: meta.issue_date || '',
          expiry_date: meta.expiry_date || '',
          notes: meta.notes || '',
          confidence: meta.confidence || '',
          error: meta.ai_error || '',
        } : e));
      } catch (e) {
        setEntries(prev => prev.map(en => en.id === ent.id ? {
          ...en, status: 'ready', error: e.message || 'AI analyse failed', confidence: 'low',
        } : en));
      }
    }
    setBusy(false);
  };

  const updateEntry = (id, patch) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  };

  const removeEntry = (id) => setEntries(prev => prev.filter(e => e.id !== id));

  const uploadAll = async () => {
    setTopErr('');
    const pending = entries.filter(e => e.status === 'ready');
    if (pending.length === 0) {
      setTopErr('Nothing to upload. Add at least one PDF.');
      return;
    }
    // Validate all
    for (const e of pending) {
      if (e.document_type === 'other' && !e.custom_name.trim()) {
        setTopErr(`Please provide a custom name for "${e.file.name}".`);
        return;
      }
    }
    setBusy(true);
    let ok = 0;
    let failed = 0;
    for (const e of pending) {
      setEntries(prev => prev.map(en => en.id === e.id ? { ...en, status: 'uploading' } : en));
      try {
        const fd = new FormData();
        fd.append('document_type', e.document_type);
        if (e.document_type === 'other') fd.append('custom_name', e.custom_name.trim());
        if (e.issue_date) fd.append('issue_date', e.issue_date);
        if (e.expiry_date) fd.append('expiry_date', e.expiry_date);
        if (e.notes.trim()) fd.append('notes', e.notes.trim());
        fd.append('file', e.file);

        const url = adminMode
          ? `${apiUrl}/api/admin/agents/${agentId}/documents`
          : `${apiUrl}/api/agents/${agentId}/documents`;
        const headers = adminMode && adminPassword ? { 'X-Admin-Password': adminPassword } : {};
        const res = await fetch(url, { method: 'POST', body: fd, headers });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || `Upload failed (${res.status})`);
        }
        setEntries(prev => prev.map(en => en.id === e.id ? { ...en, status: 'done' } : en));
        ok += 1;
      } catch (err) {
        setEntries(prev => prev.map(en => en.id === e.id ? {
          ...en, status: 'ready', error: err.message || 'Upload failed',
        } : en));
        failed += 1;
      }
    }
    setBusy(false);
    if (failed === 0) {
      onUploaded();
    } else {
      setTopErr(`Uploaded ${ok}/${ok + failed}. Review the rows marked in red and try again.`);
    }
  };

  const readyCount = entries.filter(e => e.status === 'ready').length;
  const analyzingCount = entries.filter(e => e.status === 'analyzing').length;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="upload-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Upload className="h-5 w-5 text-[#0077B6]" />
            Upload Compliance Documents
            {adminMode && (
              <span className="bg-[#0077B6]/10 text-[#0077B6] text-xs font-semibold px-2 py-0.5 rounded-full">Admin</span>
            )}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none" data-testid="close-upload-modal">×</button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-blue-50 border-l-4 border-blue-400 rounded p-3 text-sm text-blue-900">
            <p>
              <b>Tip:</b> select one or more <b>PDF, JPG, or PNG</b> files
              (≤ 15 MB each). Images are <b>automatically converted to PDF</b>.
              The AI will then auto-detect <b>document type</b>, <b>issue / expiry
              dates</b>, and write a short <b>note</b>. Review each row, edit if
              needed, then click <b>Upload all</b>.
            </p>
          </div>

          {topErr && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-900 p-3 rounded text-sm" data-testid="upload-error">{topErr}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Add PDF, JPG, or PNG files (multiple allowed)
            </label>
            <input
              type="file"
              accept="application/pdf,.pdf,image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
              multiple
              onChange={(e) => {
                // Iter 42 fix — snapshot the FileList SYNCHRONOUSLY before
                // clearing the input. Clearing the input empties the live
                // FileList; combined with an async addFiles loop this caused
                // every image after the first to be silently dropped.
                const fs = Array.from(e.target.files || []);
                e.target.value = '';
                addFiles(fs);
              }}
              className="w-full text-sm"
              data-testid="upload-file-input"
            />
            {analyzingCount > 0 && (
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Analysing {analyzingCount} file{analyzingCount === 1 ? '' : 's'} with AI…
              </p>
            )}
          </div>

          {entries.length > 0 && (
            <div className="space-y-3" data-testid="multi-upload-rows">
              {entries.map(ent => (
                <MultiUploadRow
                  key={ent.id}
                  ent={ent}
                  types={types}
                  onChange={(patch) => updateEntry(ent.id, patch)}
                  onRemove={() => removeEntry(ent.id)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="p-5 border-t flex items-center justify-between gap-2">
          <p className="text-xs text-gray-500">
            {entries.length === 0
              ? 'No files added yet.'
              : `${readyCount} ready · ${analyzingCount} analysing · ${entries.filter(e => e.status === 'done').length} uploaded`}
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Close
            </Button>
            <Button
              type="button"
              onClick={uploadAll}
              className="bg-[#0077B6]"
              disabled={busy || readyCount === 0}
              data-testid="submit-upload-btn"
            >
              {busy ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Working…</>) : (<><Upload className="h-4 w-4 mr-1" /> Upload all ({readyCount})</>)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MultiUploadRow({ ent, types, onChange, onRemove }) {
  const sizeMB = (ent.file.size / 1024 / 1024).toFixed(2);
  const isAnalyzing = ent.status === 'analyzing';
  const isUploading = ent.status === 'uploading';
  const isDone = ent.status === 'done';
  const hasErr = !!ent.error && ent.status !== 'done';

  const confBadge = (c) => {
    if (!c) return null;
    const map = {
      high: 'bg-green-100 text-green-700',
      medium: 'bg-blue-100 text-blue-700',
      low: 'bg-amber-100 text-amber-700',
    };
    return (
      <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${map[c] || map.low}`}>
        AI · {c}
      </span>
    );
  };

  return (
    <div
      className={`border rounded-lg p-3 ${isDone ? 'bg-emerald-50 border-emerald-200' : hasErr ? 'bg-red-50/40 border-red-200' : 'bg-white'}`}
      data-testid={`upload-row-${ent.id}`}
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <FileText className="h-4 w-4 text-red-500 flex-shrink-0" />
        <span className="text-sm font-medium text-gray-800 truncate flex-1 min-w-0" title={ent.file.name}>
          {ent.file.name}
          {ent.originalName && (
            <span className="ml-1 text-[10px] uppercase font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded align-middle">
              auto-converted from image
            </span>
          )}
        </span>
        <span className="text-xs text-gray-500">{sizeMB} MB</span>
        {isAnalyzing && (
          <span className="text-xs text-blue-700 inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Analysing…
          </span>
        )}
        {isUploading && (
          <span className="text-xs text-blue-700 inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
          </span>
        )}
        {isDone && (
          <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
            <CheckCircle className="h-3 w-3" /> Uploaded
          </span>
        )}
        {confBadge(ent.confidence)}
        {!isDone && !isUploading && (
          <button
            type="button"
            onClick={onRemove}
            className="text-gray-400 hover:text-red-600 ml-auto"
            title="Remove from queue"
            data-testid={`remove-row-${ent.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {hasErr && (
        <p className="text-xs text-red-700 mb-2">{ent.error}</p>
      )}

      {isAnalyzing ? (
        <div className="text-xs text-gray-500 italic px-1">AI is reading the document…</div>
      ) : isDone ? null : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Document type</label>
            <select
              value={ent.document_type}
              onChange={(e) => onChange({ document_type: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-xs focus:ring-2 focus:ring-[#0077B6]"
              disabled={isUploading}
              data-testid={`row-type-${ent.id}`}
            >
              {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {ent.document_type === 'other' && (
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Custom name *</label>
              <input
                type="text"
                value={ent.custom_name}
                onChange={(e) => onChange({ custom_name: e.target.value })}
                placeholder="e.g. Provincial Council Certificate"
                className="w-full border rounded px-2 py-1.5 text-xs focus:ring-2 focus:ring-[#0077B6]"
                disabled={isUploading}
                data-testid={`row-custom-${ent.id}`}
              />
            </div>
          )}
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Issue date</label>
            <input
              type="date"
              value={ent.issue_date}
              onChange={(e) => onChange({ issue_date: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-xs focus:ring-2 focus:ring-[#0077B6]"
              disabled={isUploading}
              data-testid={`row-issue-${ent.id}`}
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Expiry date</label>
            <input
              type="date"
              value={ent.expiry_date}
              onChange={(e) => onChange({ expiry_date: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-xs focus:ring-2 focus:ring-[#0077B6]"
              disabled={isUploading}
              data-testid={`row-expiry-${ent.id}`}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Notes (AI-drafted, editable)</label>
            <textarea
              value={ent.notes}
              onChange={(e) => onChange({ notes: e.target.value })}
              rows={2}
              className="w-full border rounded px-2 py-1.5 text-xs focus:ring-2 focus:ring-[#0077B6]"
              disabled={isUploading}
              data-testid={`row-notes-${ent.id}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function EditModal({ doc, apiUrl, types, adminMode, adminPassword, onClose, onSaved }) {
  const [docType, setDocType] = useState(doc.document_type || 'llqp_license');
  const [customName, setCustomName] = useState(doc.custom_name || '');
  const [issueDate, setIssueDate] = useState(doc.issue_date || '');
  const [expiryDate, setExpiryDate] = useState(doc.expiry_date || '');
  const [notes, setNotes] = useState(doc.notes || '');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (docType === 'other' && !customName.trim()) {
      setErr('Please provide a name for the custom document'); return;
    }
    const body = {
      document_type: docType,
      custom_name: docType === 'other' ? customName.trim() : '',
      issue_date: issueDate || '',
      expiry_date: expiryDate || '',
      notes: notes.trim(),
    };
    setSubmitting(true);
    try {
      const url = adminMode
        ? `${apiUrl}/api/admin/agents/documents/${doc.id}`
        : `${apiUrl}/api/agents/documents/${doc.id}`;
      const headers = {
        'Content-Type': 'application/json',
        ...(adminMode && adminPassword ? { 'X-Admin-Password': adminPassword } : {}),
      };
      const res = await fetch(url, { method: 'PATCH', headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Update failed (${res.status})`);
      }
      onSaved();
    } catch (e2) {
      setErr(e2.message || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="edit-document-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Pencil className="h-5 w-5 text-[#0077B6]" />
            Edit Document Details
            {adminMode && (
              <span className="bg-[#0077B6]/10 text-[#0077B6] text-xs font-semibold px-2 py-0.5 rounded-full">Admin</span>
            )}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none" data-testid="close-edit-modal">×</button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {err && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-900 p-3 rounded text-sm" data-testid="edit-error">{err}</div>
          )}
          <div className="bg-gray-50 border rounded p-3 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-500" />
              <span className="font-medium text-gray-800 truncate">{doc.original_filename}</span>
            </div>
            <p className="mt-1 text-gray-500">The PDF file itself is immutable. To replace the file, upload a new document and delete this one.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6]"
              data-testid="edit-document-type"
              required
            >
              {types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {docType === 'other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Custom Document Name *</label>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="e.g. Provincial Insurance Council Certificate"
                className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6]"
                data-testid="edit-custom-name"
                required
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
              <input
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6]"
                data-testid="edit-issue-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6]"
                data-testid="edit-expiry-date"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#0077B6]"
              data-testid="edit-notes"
            />
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#0077B6]"
              disabled={submitting}
              data-testid="save-edit-btn"
            >
              {submitting ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>) : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
